import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db, auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import Loader from "../components/Loader";

/* ================= QUOTES ================= */
const QUOTES = [
  { text: "உழைப்பு ஒருநாளும் வீணாகாது.", author: "திருவள்ளுவர்" },
  { text: "தொடர்ச்சியான முயற்சியே வெற்றியின் ரகசியம்.", author: "தமிழ் பழமொழி" },
  { text: "முயற்சி நிறுத்தாதவருக்கு தோல்வி இல்லை.", author: "அப்துல் கலாம்" },
  { text: "Hard work never goes unnoticed.", author: "Anonymous" }
];

export default function Overview() {
  const navigate = useNavigate();

  const [employeeCount, setEmployeeCount] = useState(0);
  const [totalIncome, setTotalIncome] = useState(0);
  const [totalExpense, setTotalExpense] = useState(0);
  const [notesCount, setNotesCount] = useState(0);
  const [pinnedNotes, setPinnedNotes] = useState([]);
  const [goodPartCount, setGoodPartCount] = useState(0);
  const [defectivePartCount, setDefectivePartCount] = useState(0);
  const [pendingBilled, setPendingBilled] = useState(0);
  const [pendingSalary, setPendingSalary] = useState(0);
  const [totalInvoiced, setTotalInvoiced] = useState(0);
  const [totalAudits, setTotalAudits] = useState(0);
  const [criticalAudits, setCriticalAudits] = useState(0);
  const [gasRefillReq, setGasRefillReq] = useState(0);
  const [upcomingAudits, setUpcomingAudits] = useState(0);

  const [quote, setQuote] = useState({ text: "", author: "" });
  const [fade, setFade] = useState(true);
  const [companyName, setCompanyName] = useState("");
  const [greeting, setGreeting] = useState("");
  const [loading, setLoading] = useState(true);

  const profit = totalIncome - totalExpense;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadEmployees(),
          loadCashbook(),
          loadNotes(),
          loadCompanyName(),
          loadPartStockStats(),
          loadSalaryStats(),
          loadBillingStats(),
          loadAuditStats()
        ]);
        setGreetingMessage();
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    setQuote(QUOTES[Math.floor(Math.random() * QUOTES.length)]);

    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setQuote(QUOTES[Math.floor(Math.random() * QUOTES.length)]);
        setFade(true);
      }, 400);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const setGreetingMessage = () => {
    const h = new Date().getHours();
    if (h < 12) setGreeting("Good Morning 👋");
    else if (h < 17) setGreeting("Good Afternoon 👋");
    else setGreeting("Good Evening 👋");
  };

  const loadCompanyName = async () => {
    try {
      const snap = await getDoc(doc(db, "COMPANY_PROFILE", "main_profile"));
      if (snap.exists()) {
        setCompanyName(snap.data().companyName || "");
      }
    } catch (err) {
      console.error("Error loading company name:", err);
    }
  };

  const loadEmployees = async () => {
    const snap = await getDocs(collection(db, "EMPLOYEES"));
    setEmployeeCount(snap.size);
  };

  const loadCashbook = async () => {
    let income = 0, expense = 0;
    const snap = await getDocs(collection(db, "CASHBOOK"));
    snap.forEach(d => {
      const data = d.data();
      income += Number(data.income || 0);
      expense += Number(data.expense || 0);
    });
    setTotalIncome(income);
    setTotalExpense(expense);
  };

  const loadNotes = async () => {
    const snap = await getDocs(collection(db, "adminNotes"));
    const activeNotes = snap.docs.filter(d => !d.data().archived);
    setNotesCount(activeNotes.length);
    const pinned = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(n => n.pinned && !n.archived)
      .slice(0, 3);
    setPinnedNotes(pinned);
  };

  const loadPartStockStats = async () => {
    try {
      let goodCount = 0;
      let defectiveStockCount = 0;
      let waitingDispatchCount = 0;

      // 1. Fetch from PART_STOCK
      const partsSnap = await getDocs(collection(db, "PART_STOCK"));
      partsSnap.forEach(d => {
        const data = d.data();
        goodCount += Number(data.goodQty || 0);
        defectiveStockCount += Number(data.defectiveQty || 0);
      });

      // 2. Fetch from DEFECTIVE_RETURNS (Waiting only)
      const returnsSnap = await getDocs(collection(db, "DEFECTIVE_RETURNS"));
      returnsSnap.forEach(d => {
        if (d.data().status === "WAITING") {
          waitingDispatchCount += Number(d.data().qty || 0);
        }
      });

      setGoodPartCount(goodCount);
      setDefectivePartCount(defectiveStockCount + waitingDispatchCount);
    } catch (err) {
      console.error("Error loading part stock stats:", err);
    }
  };

  const loadSalaryStats = async () => {
    try {
      const empSnap = await getDocs(collection(db, "EMPLOYEES"));
      const salSnap = await getDocs(collection(db, "salary"));

      const employees = empSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const allSalaries = salSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      let totalPending = 0;

      employees.forEach(emp => {
        // Group salaries for this employee by month and year
        const empSalsByPeriod = {};
        allSalaries.forEach(s => {
          const sEmpId = String(s.empId || s.empid || s.empID || s.emp || s.userId || "");
          if (sEmpId !== emp.id) return;

          const year = Number(s.year || s.Year || 0);
          const month = Number(s.month || s.Month || 0);
          if (!year || !month) return;

          const key = `${year}_${month}`;
          const ts = s.savedAt?.toDate ? s.savedAt.toDate().getTime() :
            (s.savedAt ? new Date(s.savedAt).getTime() :
              (s.generatedAt?.toDate ? s.generatedAt.toDate().getTime() :
                (s.generatedAt ? new Date(s.generatedAt).getTime() : 0)));

          if (!empSalsByPeriod[key] || ts > empSalsByPeriod[key].ts) {
            empSalsByPeriod[key] = { data: s, ts };
          }
        });

        // Sum up pending amounts from the latest record of EACH period
        Object.values(empSalsByPeriod).forEach(({ data }) => {
          const status = (data.status || "").toLowerCase();
          if (status === "paid" || status === "settled") return;

          const net = Number(data.netSalary ?? data.net ?? data.grossSalary ?? data.amount ?? data.total ?? 0);
          const paid = Number(data.paidAmount ?? data.paid ?? data.received ?? 0);
          const balance = net - paid;

          if (balance > 0) {
            totalPending += balance;
          }
        });
      });

      setPendingSalary(Math.round(totalPending));
    } catch (err) {
      console.error("Error loading salary stats:", err);
    }
  };

  const loadBillingStats = async () => {
    try {
      const snap = await getDocs(collection(db, "INVOICES"));
      let total = 0;
      let pending = 0;
      snap.forEach(d => {
        const data = d.data();
        if (data.status !== "CANCELLED") {
          const amt = Number(data.total || 0);
          total += amt;
          if (data.status !== "PAID") {
            pending += amt;
          }
        }
      });
      setTotalInvoiced(total);
      setPendingBilled(pending);
    } catch (err) {
      console.error("Error loading billing stats:", err);
    }
  };

  const loadAuditStats = async () => {
    try {
      const snap = await getDocs(collection(db, "AC_AUDITS"));
      setTotalAudits(snap.size);

      let critical = 0;
      let gas = 0;
      let upcoming = 0;
      const today = new Date();

      snap.forEach(d => {
        const data = d.data();
        if (data.riskLevel === "High Risk") critical++;
        if (data.gasRefillingRequired) gas++;

        if (data.nextServiceDue) {
          const dueData = data.nextServiceDue.toDate ? data.nextServiceDue.toDate() : new Date(data.nextServiceDue);
          const diffDays = (dueData - today) / (1000 * 60 * 60 * 24);
          if (diffDays >= 0 && diffDays <= 30) upcoming++;
        }
      });

      setCriticalAudits(critical);
      setGasRefillReq(gas);
      setUpcomingAudits(upcoming);
    } catch (err) {
      console.error("Error loading audit stats:", err);
    }
  };

  const gotoCashbook = () => {
    navigate(`/AdminDashboard?tab=cashbook&ts=${Date.now()}`);
  };

  if (loading) return <Loader />;

  return (
    <div className="container-fluid mt-4 px-4">

      <h4 className="fw-bold">
        {greeting}{companyName ? `, ${companyName}` : ""}
      </h4>
      <small className="text-muted">Here’s a quick overview of your business</small>

      <div className="row g-4 mt-3">
        {/* Row 1: Financial Overview */}
        <div className="col-12">
          <div className="d-flex align-items-center mb-2">
            <i className="bi bi-graph-up-arrow me-2 text-primary"></i>
            <h5 className="mb-0 fw-bold">Financial Performance</h5>
          </div>
          <div className="row g-4">
            <StatCard
              title="Total Income"
              value={`₹ ${totalIncome}`}
              icon="bi-cash-stack"
              color="success"
              onClick={gotoCashbook}
            />
            <StatCard
              title="Total Expense"
              value={`₹ ${totalExpense}`}
              icon="bi-cart-dash"
              color="danger"
              onClick={gotoCashbook}
            />
            <StatCard
              title="Total Profit"
              value={`₹ ${profit}`}
              icon="bi-piggy-bank"
              color={profit >= 0 ? "success" : "danger"}
              onClick={gotoCashbook}
            />
            <StatCard
              title="Salary Pending"
              value={`₹ ${pendingSalary}`}
              icon="bi-person-badge"
              color="warning"
              onClick={() => navigate(`/AdminDashboard?tab=Salary&ts=${Date.now()}`)}
            />
          </div>
        </div>

        {/* Row 2: Operational Metrics */}
        <div className="col-12 mt-4">
          <div className="d-flex align-items-center mb-2">
            <i className="bi bi-gear-wide-connected me-2 text-primary"></i>
            <h5 className="mb-0 fw-bold">Operations & Billing</h5>
          </div>
          <div className="row g-4">
            <StatCard
              title="Employees"
              value={employeeCount}
              icon="bi-people"
              color="primary"
            />
            <StatCard
              title="Good Parts"
              value={goodPartCount}
              icon="bi-box-seam"
              color="info"
              onClick={() => navigate(`/AdminDashboard?tab=PartStock&ts=${Date.now()}`)}
            />
            <StatCard
              title="Total Invoiced"
              value={`₹ ${totalInvoiced}`}
              icon="bi-file-earmark-text"
              color="info"
              onClick={() => navigate(`/AdminDashboard?tab=Billing&ts=${Date.now()}`)}
            />
            <StatCard
              title="Collection Pending"
              value={`₹ ${pendingBilled}`}
              icon="bi-exclamation-octagon"
              color="danger"
              onClick={() => navigate(`/AdminDashboard?tab=Billing&ts=${Date.now()}`)}
            />
          </div>
        </div>

        {/* Row 3: Misc */}
        <div className="col-12 mt-4">
          <div className="row g-4">
            <StatCard
              title="Admin Notes"
              value={notesCount}
              icon="bi-journal-text"
              color="warning"
              onClick={() => navigate(`/AdminDashboard?tab=notes&ts=${Date.now()}`)}
            />
            <StatCard
              title="Defective"
              value={defectivePartCount}
              icon="bi-wrench"
              color="secondary"
              onClick={() => navigate(`/AdminDashboard?tab=PartStock&ts=${Date.now()}`)}
            />
          </div>
        </div>

        {/* Row 4: AC Audit Insights */}
        <div className="col-12 mt-4">
          <div className="d-flex align-items-center mb-2">
            <i className="bi bi-shield-check me-2 text-primary"></i>
            <h5 className="mb-0 fw-bold">AC Audit Insights</h5>
          </div>
          <div className="row g-4">
            <StatCard
              title="Total Audits"
              value={totalAudits}
              icon="bi-clipboard-check"
              color="primary"
              onClick={() => navigate(`/AdminDashboard?tab=Audit&ts=${Date.now()}`)}
            />
            <StatCard
              title="Critical Units"
              value={criticalAudits}
              icon="bi-exclamation-triangle"
              color="danger"
              onClick={() => navigate(`/AdminDashboard?tab=Audit&ts=${Date.now()}`)}
            />
            <StatCard
              title="Gas Refill Req."
              value={gasRefillReq}
              icon="bi-moisture"
              color="warning"
              onClick={() => navigate(`/AdminDashboard?tab=Audit&ts=${Date.now()}`)}
            />
            <StatCard
              title="Upcoming Service"
              value={upcomingAudits}
              icon="bi-calendar-event"
              color="success"
              onClick={() => navigate(`/AdminDashboard?tab=AC Audit&ts=${Date.now()}`)}
            />
          </div>
        </div>
      </div>

      {pinnedNotes.length > 0 && (
        <div className="mt-5">
          <div className="d-flex align-items-center mb-3">
            <i className="bi bi-pin-angle me-2 text-primary"></i>
            <h5 className="fw-bold mb-0">Pinned Notes</h5>
          </div>
          <div className="row g-3">
            {pinnedNotes.map(note => (
              <div className="col-md-4" key={note.id}>
                <div className="card h-100 shadow-sm border-0 p-4"
                  onClick={() => navigate(`/AdminDashboard?tab=notes&ts=${Date.now()}`)}
                  style={{
                    cursor: "pointer",
                    borderRadius: "20px",
                    background: "linear-gradient(145deg, #ffffff, #f8f9fa)",
                    borderLeft: "4px solid var(--bs-primary)",
                    transition: "all 0.3s ease",
                    border: "1px solid rgba(0,0,0,0.03)"
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.08)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.03)";
                  }}
                >
                  <h6 className="fw-bold text-dark mb-2">{note.title}</h6>
                  <p className="small text-secondary mb-0" style={{ lineHeight: "1.6" }}>
                    {note.content.length > 100 ? note.content.substring(0, 100) + "..." : note.content}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ height: "50px" }} />

      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="p-5 text-center shadow-sm position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #eafff3 0%, #ffffff 100%)",
              borderRadius: "24px",
              border: "1px solid rgba(0,0,0,0.03)"
            }}
          >
            <i className="bi bi-quote position-absolute text-success" style={{ top: "10px", left: "20px", fontSize: "3rem", opacity: 0.1 }}></i>
            <div
              className="fw-medium px-4"
              style={{
                fontSize: "1.25rem",
                color: "#2d3436",
                fontFamily: "'Inter', sans-serif",
                opacity: fade ? 1 : 0,
                transition: "all 0.6s ease",
                lineHeight: "1.5"
              }}
            >
              “{quote.text}”
            </div>
            <div className="mt-3 text-success fw-bold">— {quote.author}</div>
          </div>
        </div>
      </div>

    </div>
  );
}

/* ================= STAT CARD ================= */
const StatCard = ({ title, value, color, icon, onClick }) => (
  <div className="col-md-3">
    <div
      className="card h-100 border-0 shadow-sm"
      style={{
        cursor: onClick ? "pointer" : "default",
        borderRadius: "24px",
        overflow: "hidden",
        background: "rgba(255, 255, 255, 0.8)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.4)",
        transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        position: "relative",
        boxShadow: "0 10px 30px rgba(0,0,0,0.05)"
      }}
      onClick={onClick}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
        e.currentTarget.style.boxShadow = "0 20px 40px rgba(0,0,0,0.1)";
        e.currentTarget.style.borderColor = `var(--bs-${color})`;
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = "translateY(0) scale(1)";
        e.currentTarget.style.boxShadow = "0 10px 30px rgba(0,0,0,0.05)";
        e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.4)";
      }}
    >
      <div className="card-body p-4 position-relative" style={{ zIndex: 1 }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div
            className={`d-flex align-items-center justify-content-center bg-${color}-subtle text-${color} rounded-2`}
            style={{
              width: "52px",
              height: "52px",
              fontSize: "1.4rem",
              boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
            }}
          >
            <i className={`bi ${icon}`}></i>
          </div>
          <div className="d-flex align-items-center gap-2">
            <span className="position-relative d-flex">
              <span className={`position-absolute h-100 w-100 rounded-circle bg-${color} opacity-25 animate-ping`} style={{ animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite' }}></span>
              <span className={`rounded-circle h-2 w-2 bg-${color}`} style={{ width: '8px', height: '8px' }}></span>
            </span>
            <span className={`text-${color} fw-bold small`} style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "1px" }}>
              Live
            </span>
          </div>
        </div>

        <div>
          <p className="text-muted mb-1 fw-bold" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
            {title}
          </p>
          <h2 className="fw-black text-dark m-0" style={{ fontSize: "1.8rem", letterSpacing: "-0.5px" }}>
            {value}
          </h2>
        </div>

        {/* Decorative subtle background gradient */}
        <div style={{
          position: "absolute",
          top: "-50px",
          right: "-50px",
          width: "120px",
          height: "120px",
          background: `var(--bs-${color})`,
          opacity: 0.05,
          borderRadius: "50%",
          filter: "blur(40px)",
          zIndex: -1
        }} />
      </div>

      {/* CSS for ping animation */}
      <style>{`
        @keyframes ping {
          75%, 100% {
            transform: scale(2.5);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  </div>
);
